import React, { useEffect, useState } from "react";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import InputLabel from "@mui/material/InputLabel";
import FormControl from "@mui/material/FormControl";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import API from '../../utils/api';
import states from "../../utils/states.js";
import {
  api2OrganizationStripeWebhookCreate,
  api2OrganizationStripeWebhooks,
  getCurrentOrg
} from "../../utils/operations.js";
import { observer, inject } from "mobx-react";
import InputMask from 'react-input-mask';
import { toast } from 'react-toastify';
import appRoutes from "../../utils/appRoutes";
import { Link } from "react-router-dom";

const EditOrganisations = ({ Auth, history, match }) => {
  const [data, setData] = useState({
    id: "", name: "", address: "", city: "", st: "", zip: "", phone: "", fax: "",
    site: "", email: "", taxid: "",
    stripe_account: "", stripe_publishable_key: "", stripe_secret_key: "", stripe_webhook_secret: "",
  });
  const [webhooks, setWebhooks] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [openErrorDialog, setOpenErrorDialog] = useState(false);
  const [textErrors, setTextErrors] = useState("");

  const loadData = () => {
    const orgId = match.params.id;
    API.get(getCurrentOrg + orgId).then(res => setData(res.data));
    API.get(api2OrganizationStripeWebhooks(orgId)).then(res => setWebhooks(res.data.data));
  };

  useEffect(() => {
    if (!Auth.can('organisations')) { history.push("/children"); return; }
    loadData();
  }, []);

  const handleChange = ({ target: { id, value } }) => setData(prev => ({ ...prev, [id]: value }));

  const saveData = () => {
    API.patch(getCurrentOrg + data.id, data).then(() => {
      toast.success('Organization changed successfully!');
      history.push("/organisations");
    }).catch(error => toast.error(error));
  };

  const deleteData = () => {
    setOpenDialog(false);
    API.delete(getCurrentOrg + data.id).then(() => {
      toast.success('Organization deleted successfully!');
      history.push("/organisations");
    }).catch(error => {
      const issues = error.response.data.error.message;
      const res = issues.match(/\.`\w+`/);
      let table = res[0].replace(/`/g, "").replace(".", "");
      const tableMap = { bills: "Invoices", parents: "Parents", classes: "Classes", childs: "Children" };
      table = tableMap[table] || "Information table";
      setTextErrors(`You can't delete '${Auth.orgName}' while '${table}' exist`);
      setOpenErrorDialog(true);
    });
  };

  const handleCreateWebhook = () => {
    if (!confirm("Are you sure you want to create Stripe Webhook for this organization?")) return;
    API.post(api2OrganizationStripeWebhookCreate(match.params.id), {}).then(() => {
      toast.success('Organization webhook created! Please copy Stripe WebHook Secret from Stripe and save it here!', { autoClose: 10000 });
      loadData();
    }).catch(error => toast.error(error));
  };

  return (
    <div style={{ width: 500, margin: "auto" }}>
      <h3>School</h3>
      <TextField id="name" label="School Name" fullWidth value={data.name} onChange={handleChange} sx={{ mb: 2 }} />
      <TextField id="address" label="Address" fullWidth value={data.address} onChange={handleChange} sx={{ mb: 2 }} />
      <TextField id="city" label="City" fullWidth value={data.city} onChange={handleChange} sx={{ mb: 2 }} />
      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel id="state-label">State</InputLabel>
        <Select labelId="state-label" value={data.st || ''} label="State" onChange={({ target: { value } }) => setData(prev => ({ ...prev, st: value }))}>
          {states.map((row, index) => <MenuItem key={index} value={row}>{row}</MenuItem>)}
        </Select>
      </FormControl>
      <TextField id="zip" label="ZIP" fullWidth value={data.zip} onChange={handleChange} sx={{ mb: 2 }} />
      <InputMask mask="999-999-9999 ext. 999" fullWidth value={data.phone} onChange={handleChange} disabled={false}>
        {(inputProps) => <TextField {...inputProps} id="phone" label="Phone" fullWidth sx={{ mb: 2 }} />}
      </InputMask>
      <TextField id="fax" label="Fax" fullWidth value={data.fax} onChange={handleChange} sx={{ mb: 2 }} />
      <TextField id="site" label="Website" fullWidth value={data.site} onChange={handleChange} sx={{ mb: 2 }} />
      <TextField id="email" label="Email" fullWidth value={data.email} onChange={handleChange} sx={{ mb: 2 }} />
      <TextField id="taxid" label="Tax ID#" fullWidth value={data.taxid} onChange={handleChange} sx={{ mb: 2 }} />
      <br />
      <h3>Stripe Integration <Link to={appRoutes.stripeHelp} target="_blank">STRIPE SETUP HELP</Link></h3>
      <TextField id="stripe_account" label="Stripe Account ID" fullWidth value={data.stripe_account} onChange={handleChange} sx={{ mb: 2 }} />
      <TextField id="stripe_publishable_key" label="Stripe Publishable KEY" fullWidth value={data.stripe_publishable_key} onChange={handleChange} sx={{ mb: 2 }} />
      <TextField id="stripe_secret_key" label="Stripe Secret KEY" fullWidth value={data.stripe_secret_key} onChange={handleChange} sx={{ mb: 2 }} />
      <TextField id="stripe_webhook_secret" label="Stripe WebHook Secret" fullWidth value={data.stripe_webhook_secret} onChange={handleChange} sx={{ mb: 2 }} />
      <br />
      <b>Webhook URL and events to add in stripe.com account:</b>
      <div className={`alert alert--success ${webhooks.length === 0 ? ' blink ' : ''}`}>{process.env.REACT_APP_API_BASE_LARAVEL_URL}webhook/stripe/{data.id}</div>
      <br />
      {webhooks.length === 0 ? <>
        <Button variant="contained" color="primary" onClick={handleCreateWebhook}>Create Webhook Automatically</Button>
        <br />
      </> : <>
        <b>List of Webhooks currently configured:</b>
        <ul>
          {webhooks.map((webhook, index) => (
            <li key={index}>URL: {webhook.url}<br />API version: {webhook.api_version}</li>
          ))}
        </ul>
        <br />
      </>}
      <div style={{ display: "flex", gap: 10, alignItems: "center", justifyContent: "center", paddingTop: 20, paddingBottom: 20 }}>
        <Button variant="contained" color="primary" onClick={saveData}>Save</Button>
        <Button variant="contained" sx={{ backgroundColor: "#ffd699", color: "#000", '&:hover': { backgroundColor: "#ffcc80" } }} onClick={() => history.goBack()}>Cancel</Button>
        <Button variant="contained" color="secondary" onClick={() => setOpenDialog(true)}>Delete</Button>
      </div>
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="xs">
        <DialogContent>Delete Organisation?</DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button onClick={deleteData} color="primary">OK</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={openErrorDialog} onClose={() => setOpenErrorDialog(false)} maxWidth="xs">
        <DialogContent>{textErrors}</DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenErrorDialog(false)} color="primary">Ok</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default inject("Auth")(observer(EditOrganisations));

